<template lang="pug">
  component(
    :is="tag"
    :class="['base-btn', btnClasses]"
    :disabled="disabled"
    v-on="$listeners"
    :to="to"
    v-bind="$attrs"
  )
    .base-btn__content
      slot Button
    BaseSpinner.base-btn__spinner(
      :value="loading",
      small
    )
</template>

<script>
import BaseSpinner from 'Components/Base/BaseSpinner';

export default {
  name: 'BaseButton',
  components: { BaseSpinner },
  props: {
    tag: {
      type: String,
      default: 'button',
    },
    large: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    xsmall: {
      type: Boolean,
      default: false,
    },
    to: [Object, String], // Note: Requires tag to be defined to work
    /** color - background state for btn
     * default primary, can be: primary/outlined
     */
    color: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'outlined', 'outlined-gray', 'transparent'].includes(value),
    },
    disabled: Boolean,
    block: Boolean,
    loading: Boolean,
  },
  computed: {
    btnClasses() {
      let classes = `is--${this.color} `;
      classes += this.large ? 'is--large ' : '';
      classes += this.small ? 'is--small ' : '';
      classes += this.xsmall ? 'is--xsmall ' : '';
      classes += this.block ? 'is--block ' : '';
      classes += this.disabled ? 'is--disabled ' : '';
      classes += this.loading ? 'is--loading ' : '';
      return classes;
    },
    component() {
      return 'router-link';
    },
  },
};
</script>

<style lang="sass">
@import '~Styles/_variables'
@import './_variables'
// default styles are primary color and regular size
.base-btn
  display: inline-flex
  justify-content: center
  align-items: center
  border: unset
  height: $base-button-height
  font-family: $base-button-font-family
  font-weight: $base-button-font-weight
  color: $base-button-color
  fill: $base-button-fill
  background-color: $base-button-background-color
  box-shadow: $base-button-box-shadow
  border-radius: $base-button-border-radius
  padding: $base-button-padding
  font-size: $base-button-font-size
  cursor: pointer
  transition: all 0.3s ease-in-out
  &:hover
    color: $base-button-hover-color
    fill: $base-button-hover-fill
    background-color: $base-button-hover-background-color
    box-shadow: $base-button-hover-box-shadow
    transition: all 0.3s ease-in-out
  &:focus
    box-shadow: none
  svg
    width: 16px
    height: 16px
  &__content
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
  // sizes
  &.is--xsmall
    height: $base-button-xsmall-height
    padding: $base-button-xsmall-padding
    font-size: $base-button-xsmall-font-size
    font-weight: $base-button-xsmall-font-weight
    .base-btn__left-icon
      margin-right: 7px
    .base-btn__right-icon
      margin-left: 7px
  &.is--small
    height: $base-button-small-height
    padding: $base-button-small-padding
    font-size: $base-button-small-font-size

  &.is--large
    height: $base-button-large-height
    padding: $base-button-large-padding
    svg
      width: 20px
      height: 20px
  // colors
  &.is--secondary
    background-color: $base-button-secondary-background-color
    &:hover
      background-color: $base-button-secondary-hover-background-color

  &.is--outlined
    color: $base-button-outlined-color
    fill: $base-button-outlined-fill
    background-color: $base-button-outlined-background-color
    border: $base-button-outlined-border
    box-shadow: $base-button-outlined-box-shadow
    &:hover
      transition: all 0.3s ease-in-out
      color: $base-button-outlined-hover-color
      fill: $base-button-outlined-hover-fill
      border: $base-button-outlined-hover-border
      background-color: $base-button-outlined-hover-background-color
    &:active
      transition: all 0.3s ease-in-out
      color: $base-button-outlined-active-color
      fill: $base-button-outlined-active-fill
      border: $base-button-outlined-active-border
      background-color: $base-button-outlined-active-background-color
    &:focus
      transition: all 0.3s ease-in-out
      color: $base-button-outlined-active-color
      fill: $base-button-outlined-active-fill
      border: $base-button-outlined-active-border
      background-color: $base-button-outlined-active-background-color
  &.is--outlined-gray
    color: $base-button-outlined-gray-color
    fill: $base-button-outlined-gray-fill
    background-color: $base-button-outlined-gray-background-color
    border: $base-button-outlined-gray-border
    box-shadow: $base-button-outlined-gray-box-shadow
    &:hover
      transition: all 0.3s ease-in-out
      color: $base-button-outlined-gray-hover-color
      fill: $base-button-outlined-gray-hover-fill
      border: $base-button-outlined-gray-hover-border
      background-color: $base-button-outlined-gray-hover-background-color
    &:active
      transition: all 0.3s ease-in-out
      color: $base-button-outlined-gray-active-color
      fill: $base-button-outlined-gray-active-fill
      border: $base-button-outlined-gray-active-border
      background-color: $base-button-outlined-gray-active-background-color
    &:focus
      transition: all 0.3s ease-in-out
      color: $base-button-outlined-gray-focus-color
      fill: $base-button-outlined-gray-focus-fill
      border: $base-button-outlined-gray-focus-border
      background-color: $base-button-outlined-gray-focus-background-color
      &:before
        content: ''
        position: absolute
        left: 0
        top: 0
        width: 100%
        height: 100%
        border: solid 1px $base-button-outlined-gray-focus-before-border-color
  &.is--transparent
    color: $base-button-transparent-color
    fill: $base-button-transparent-fill
    background-color: $base-button-transparent-background-color
    &:hover
      transition: all 0.3s ease-in-out
      color: $base-button-transparent-hover-color
      fill: $base-button-transparent-hover-fill
      background-color: $base-button-transparent-hover-background-color
    &:active
      transition: all 0.3s ease-in-out
      color: $base-button-transparent-active-color
      fill: $base-button-transparent-active-fill
      background-color: $base-button-transparent-active-background-color
    &:focus
      transition: all 0.3s ease-in-out
      color: $base-button-transparent-focus-color
      fill: $base-button-transparent-focus-fill
      border: $base-button-transparent-focus-border
      background-color: $base-button-transparent-background-color
  &.is--block
    width: 100%

  &.is--disabled
    pointer-events: none
    opacity: 0.3

  &.is--loading
    pointer-events: none
    .base-btn__content
      opacity: 0

  .base-btn__spinner
    background-color: transparent
  &__left-icon
    margin-right: 12px
  &__right-icon
    margin-left: 12px
</style>
